import { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { Button, Input, Message } from 'semantic-ui-react';

const SET_PERSON_EMAIL = gql`
  mutation SetPersonEmail($userId: Int!, $email: String!) {
    setPersonEmail(userId: $userId, email: $email) { id email canAddEmail }
  }
`;

/**
 * A person's email address, and — where it is blank — a way to fill it in.
 *
 * ---------------------------------------------------------------------------------------
 * WHY THIS IS A CONTROL AT ALL
 * ---------------------------------------------------------------------------------------
 *
 * Roughly 2,800 members have a username that is not an address and no `email`, so **nothing
 * can be sent to them** — not a password reset, not an invitation, nothing. The only way to
 * give somebody an address was a script. This is the screen where the person who knows the
 * address is already standing.
 *
 * ---------------------------------------------------------------------------------------
 * EVERY RULE LIVES ON THE SERVER
 * ---------------------------------------------------------------------------------------
 *
 * `canAddEmail` is the server's own answer to "may this viewer fill in this blank" — it folds
 * together the authority (`ORGANISATION: MANAGE`), the scope (a live membership here), and the
 * state (a username, and no address yet). Deriving any of that on this side would be a copy
 * that goes stale, and the failure mode is a control that throws when pressed.
 *
 * **It never offers to *change* an address**, only to add one. Changing where somebody's
 * password resets go — and, for an invited account, their login identifier — is a different
 * act with different care; `setPersonEmail` refuses it, and so does this.
 */
export const PersonEmail = ({
    userId,
    email,
    canAddEmail,
    onSaved,
}: {
    userId: number;
    /** `''` for a viewer the server will not show it to, as well as for a genuine blank. */
    email: string | null | undefined;
    canAddEmail: boolean;
    onSaved: () => void;
}) => {
    const [setPersonEmail, { loading }] = useMutation(SET_PERSON_EMAIL);
    const [adding, setAdding] = useState(false);
    const [value, setValue] = useState('');
    const [error, setError] = useState<string | null>(null);

    const stored = (email ?? '').trim();

    const save = async () => {
        setError(null);
        try {
            await setPersonEmail({ variables: { userId, email: value.trim() } });
            setAdding(false);
            setValue('');
            onSaved();
        } catch (e: any) {
            // The server's message is the useful one: "already in use by another account"
            // usually means this person has a second account, which is a different problem
            // from a typo and needs a different fix.
            setError(e?.message ?? 'Could not save that address.');
        }
    };

    if (stored !== '') {
        return <span>{stored}</span>;
    }

    if (!canAddEmail) {
        // Either there is no address and the viewer may not add one, or they may not see it.
        // Both are "nothing to show here", and distinguishing them would leak which.
        return <span style={{ color: '#aaa' }}>—</span>;
    }

    if (!adding) {
        return (
            <Button size="tiny" basic onClick={() => setAdding(true)}>
                Add an email address
            </Button>
        );
    }

    return (
        <div>
            <Input
                fluid
                size="small"
                type="email"
                autoFocus
                placeholder="name@example.org"
                value={value}
                disabled={loading}
                onChange={(_, d) => setValue(d.value)}
                onKeyDown={(e: any) => { if (e.key === 'Enter' && value.trim() !== '') save(); }}
            />
            <div style={{ color: '#767676', fontSize: '0.85em', marginTop: '0.3em' }}>
                Where password resets and invitations are sent. It does not change how they
                sign in — they keep using their username.
            </div>
            {error && <Message negative size="tiny" content={error} style={{ marginTop: '0.5em' }} />}
            <div style={{ marginTop: '0.5em' }}>
                <Button
                    size="tiny"
                    primary
                    loading={loading}
                    disabled={loading || value.trim() === ''}
                    onClick={save}
                >
                    Save
                </Button>
                <Button
                    size="tiny"
                    basic
                    disabled={loading}
                    onClick={() => { setAdding(false); setValue(''); setError(null); }}
                >
                    Cancel
                </Button>
            </div>
        </div>
    );
};
